import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useUser } from "@clerk/react";
import { toast } from "sonner";
import { Users, UserCheck, FileClock, AlertTriangle, Database, UserPlus, CalendarCheck, ShieldCheck, Wallet, BadgeCheck, FileStack, ArrowRight, ShieldAlert, CalendarClock } from "lucide-react";
import { useApi, useFetch, errMsg } from "../lib/api";
import { useMember } from "../lib/member";
import { Empty } from "../components/shared";
import { Button } from "../components/ui/button";
import { LicenceCalendar } from "../components/LicenceCalendar";
import { HealthReport } from "../components/HealthReport";
import { ClraPortal, ClraApprovals, ClraBanner } from "../components/ClraPortal";
import { LowestScores, MyScoreCard } from "../components/PeScore";
import { DeadlineBanner } from "../components/DeadlineBanner";
import { OrgChecklistCard } from "../components/OrgChecklist";
import { PeBriefingCard } from "../components/PeBriefingCard";
import { ContractorTodoCard } from "../components/ContractorTodoCard";
import { PeTodoProgressCard } from "../components/PeTodoProgressCard";
import { PhotoGapCard } from "../components/PhotoGap";
import { InstallBanner } from "../components/InstallApp";

const Kpi = ({ label, value, Icon, tone, to, i }) => (
  <Link to={to} data-testid="dashboard-kpi-card" style={{ animationDelay: `${i * 60}ms` }} className="c-surface p-5 c-rise hover:border-[#00E599]/40 transition-colors">
    <div className="flex items-center justify-between"><span className="text-[11px] uppercase tracking-[0.15em] text-slate-400">{label}</span><Icon size={16} className={tone} /></div>
    <div className={`font-display text-4xl font-extrabold mt-4 ${tone}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value ?? "–"}</div>
  </Link>
);

const Bar = ({ score }) => {
  const s = Math.max(0, Math.min(100, score ?? 0));
  const c = s >= 80 ? "#00E599" : s >= 50 ? "#F59E0B" : "#EF4444";
  return <div className="h-2 rounded-full bg-[#0B1017] overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${s}%`, background: c }} /></div>;
};

export default function Home() {
  const [calVer, setCalVer] = useState(0);
  const api = useApi();
  const { user } = useUser();
  const { me, t, lang } = useMember();
  const { data: d, reload } = useFetch("/dashboard");
  const seed = async () => {
    try { await api.post("/seed"); toast.success("Sample data loaded"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const vendor = me?.role === "vendor";
  const pe = me?.role === "employer";
  const { hash } = useLocation();
  useEffect(() => {
    if (hash !== "#clra") return undefined;
    const t = setTimeout(() => document.getElementById("clra")?.scrollIntoView({ behavior: "smooth" }), 600);
    return () => clearTimeout(t);
  }, [hash]);
  const quick = pe ? [["/vendors", "Contractors", Users], ["/workers", "Workers", UserCheck], ["/wages", "Payroll", Wallet],
    ["/challans", "Challans", FileStack], ["/compliance", "Payment Holds", ShieldCheck], ["/pe", "PE Filings", BadgeCheck]] : [["/workers?add=1", "Add Worker", UserPlus], ["/attendance", "Attendance", CalendarCheck], ["/reports", "Compliance", ShieldCheck],
    ["/wages", "Payroll", Wallet], ["/settings?tab=licenses", "Licenses", BadgeCheck], ["/reports?tab=filings", "PF/ESIC Filings", FileStack]]
    .filter(([to]) => !vendor || !/reports|settings/.test(to));

  const fullName = [user?.lastName, user?.firstName].map((s) => (s || "").trim()).filter(Boolean).join("\u00A0");
  return (
    <div>
      <div className="mb-8 c-rise flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="c-eyebrow mb-2">{new Date().toLocaleDateString(`${lang}-IN`, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight break-words [overflow-wrap:anywhere]" data-testid="welcome-line">{t("Welcome")},{"\u00A0"}{String(fullName || d?.name || me?.name || "").split(/[@+]/)[0]}</h1>
        </div>
        {d && d.employers === 0 && me?.role === "admin" && (
          <Button onClick={seed} data-testid="load-sample-data-btn" className="bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] font-semibold"><Database size={15} className="mr-2" />Load sample data</Button>)}
      </div>

      {vendor && me && <DeadlineBanner />}
      {me && <OrgChecklistCard />}
      {pe && <PeBriefingCard />}
      {pe && <PeTodoProgressCard />}
      {pe && <HealthReport />}
      {vendor && me && <ContractorTodoCard />}
      {vendor && me && <ClraBanner key={calVer} />}

      <InstallBanner />
      {(me?.role === "admin" || me?.role === "staff") && <PhotoGapCard />}
      {d?.pending_docs > 0 && (
        <Link to="/workers?docs=pending" data-testid="pending-docs-alert" className="c-surface c-rise mb-6 flex items-center justify-between gap-3 p-4 !border-amber-500/30 hover:!border-amber-500/60">
          <span className="text-sm text-amber-300"><FileClock size={15} className="inline mr-2" />{d.pending_docs} documents pending verification</span>
          <span className="text-xs text-amber-400 flex items-center gap-1">{t("Review now")}<ArrowRight size={12} /></span>
        </Link>)}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi i={0} label={t("Total Workers")} value={d?.total_workers} Icon={Users} tone="text-slate-100" to="/workers" />
        <Kpi i={1} label={t("Present Today")} value={d?.present_today} Icon={UserCheck} tone="text-emerald-400" to="/attendance" />
        <Kpi i={2} label={t("Pending Docs")} value={d?.pending_docs} Icon={FileClock} tone="text-amber-400" to="/workers?docs=pending" />
        <Kpi i={3} label={t("Issues")} value={d?.issues} Icon={AlertTriangle} tone="text-rose-400" to={vendor ? "/" : "/alerts"} />
      </div>
      {d && d.licenses_expired != null && <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="licence-tiles">
        <Kpi i={4} label={t("Licences Expired")} value={d.licenses_expired} Icon={ShieldAlert} tone={d.licenses_expired ? "text-rose-400" : "text-slate-100"} to="/vendors" />
        <Kpi i={5} label={t("Expiring in 30 Days")} value={d.licenses_expiring} Icon={CalendarClock} tone={d.licenses_expiring ? "text-amber-400" : "text-slate-100"} to="/vendors" />
      </div>}

      <div className="grid xl:grid-cols-3 gap-6">
        <div className="c-surface p-6 xl:col-span-2 c-rise">
          <div className="flex items-center justify-between mb-5">
            <div><div className="c-eyebrow mb-1">{t("Compliance by Site")}</div>
              <div className="text-[11px] text-slate-500" data-testid="site-score-hint">Worker records score (documents, UAN, ESIC, wages, age). The Sites map shows the contractor filing score instead.</div>
              <div className="text-sm text-slate-400">Overall score <span className="font-mono text-white" data-testid="overall-score">{d?.overall_score ?? "—"}%</span></div></div>
            {!vendor && !pe && <Link to="/reports" className="text-xs text-slate-400 hover:text-white" data-testid="full-compliance-link">Full report →</Link>}
          </div>
          <div className="grid gap-4">
            {(d?.by_site || []).map((s) => (
              <div key={s.id} data-testid="site-score-row">
                <div className="flex justify-between text-sm mb-1.5"><span>{s.name} <span className="text-xs text-slate-500">· {s.workers} workers</span></span>
                  <span className="font-mono">{s.score ?? "—"}{s.score != null && "%"}</span></div>
                <Bar score={s.score} />
                {(d?.departments || []).filter((x) => x.site_id === s.id).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="site-department-headcount">
                    {d.departments.filter((x) => x.site_id === s.id).map((x) => (
                      <span key={x.department} className="rounded-full border border-[#1F2A3E] bg-[#0B1017] px-2 py-0.5 text-[11px] text-slate-300" data-testid="department-chip">
                        {x.department} <span className="font-mono text-white" title="Present today / headcount"><span className="text-emerald-300" data-testid="department-present">{x.present}</span>/{x.workers}</span></span>))}
                  </div>)}
              </div>
            ))}
            {(d?.departments || []).some((x) => !x.site_id) && (
              <div data-testid="no-site-department-row"><div className="text-sm mb-1.5">No site <span className="text-xs text-slate-500">· {d.departments.filter((x) => !x.site_id).reduce((a, x) => a + x.workers, 0)} workers</span></div>
                <div className="flex flex-wrap gap-1.5">{d.departments.filter((x) => !x.site_id).map((x) => (
                  <span key={x.department} className="rounded-full border border-[#1F2A3E] bg-[#0B1017] px-2 py-0.5 text-[11px] text-slate-300" data-testid="department-chip">{x.department} <span className="font-mono text-white" title="Present today / headcount"><span className="text-emerald-300" data-testid="department-present">{x.present}</span>/{x.workers}</span></span>))}</div></div>)}
            {d?.by_site?.length === 0 && <Empty text="No sites yet." />}
          </div>
          <div className="c-eyebrow mt-8 mb-3">{t("Quick actions")}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {quick.map(([to, l, I]) => (
              <Link key={l} to={to} data-testid={`quick-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="flex items-center gap-2.5 rounded-lg border border-[#1F2A3E] bg-[#0B1017] px-3 py-3 text-sm hover:border-[#00E599]/40 hover:text-white transition-colors">
                <I size={15} className="text-[#00E599]" />{t(l)}</Link>))}
          </div>
        </div>
        <div className="c-surface p-6 c-rise">
          <div className="flex items-center justify-between mb-4"><div className="c-eyebrow">{t("Recent activity")}</div>
            {!vendor && !pe && <Link to="/reports?tab=audit" className="text-xs text-slate-400 hover:text-white" data-testid="audit-log-link">Audit log →</Link>}</div>
          <ol className="relative border-l border-[#1F2A3E] ml-1.5 grid gap-4">
            {(d?.activity || []).map((a) => (
              <li key={a.id} className="pl-4 relative" data-testid="activity-item">
                <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-[#00E599]" />
                <div className="text-sm"><b>{a.user_name}</b> · {a.action}</div>
                <div className="text-[11px] text-slate-500 font-mono">{new Date(a.at).toLocaleString("en-IN")}</div>
              </li>))}
            {d?.activity?.length === 0 && <Empty text="No activity yet." />}
          </ol>
        </div>
      </div>
      {!vendor && me && <LowestScores />}
      {vendor && me && <MyScoreCard />}
      {vendor && me && <div id="clra" className="scroll-mt-24"><ClraPortal onSubmitted={() => setCalVer((n) => n + 1)} /></div>}
      {!vendor && me && <ClraApprovals onChange={() => setCalVer((n) => n + 1)} />}
      {!vendor && !pe && me && <LicenceCalendar key={calVer} />}
    </div>
  );
}
